import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { Hosting, HostingFormInputs, HostingsFormFields, HostingsQueryKeys, useCreateHostingsMutation } from '@/modules/Hostings';

import { HostingsFormView } from './HostingsFormView';
import { hostingsFormValidationSchema } from './validation';

export const HostingsCreateFormContainer: FC = memo(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<HostingsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof HostingFormInputs>>(null);

  const { mutate: createHostingMutation, isLoading: createHostingsIsLoading } = useCreateHostingsMutation()({
    onSuccess: async (data: Hosting) => {
      appMessageApi.success('Хостинг успішно створено!');
      navigate(AppRoutes.HostingsById.replace(':hostingId', data.id));
      await queryClient.refetchQueries([HostingsQueryKeys.GetAll]);
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof HostingFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: HostingsFormFields = {
    name: undefined,
    link: undefined
  };

  const handleFinish = useCallback((values: HostingsFormFields): void => {
    setFormErrors(null);
    createHostingMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.Hostings);
  }, []);

  return (
    <HostingsFormView
      formName="hosting-create"
      formType="create"
      form={ form }
      formErrors={ formErrors }
      initialValues={ initialValues }
      isFormLoading={ createHostingsIsLoading }
      validationSchema={ hostingsFormValidationSchema }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
